import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
// logo is served from /public/logo.png and /public/logo_large.png

export default function LoginPage() {
  const { login, error, setError, lock, setLock } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    setSecondsLeft(lock?.retryAfter || 0);
  }, [lock]);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  const locked = secondsLeft > 0;
  const remaining = lock?.remaining || 0;
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(email, password);
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* Left: visual panel */}
      <div className="hidden lg:block relative overflow-hidden border-r border-zinc-200">
        <img
          src="https://images.pexels.com/photos/36532609/pexels-photo-36532609.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="santier"
          className="absolute inset-0 w-full h-full object-cover grayscale"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative h-full flex items-center justify-center p-10 text-white">
          <img
            src="/logo_large.png"
            alt="CostaNov"
            className="w-96 max-w-full drop-shadow-2xl"
            data-testid="login-hero-logo"
          />
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12 industrial-grid">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm bg-white border border-zinc-200 p-8 rounded-sm"
          data-testid="login-form"
        >
          <div className="lg:hidden mb-6 flex items-center gap-2">
            <img
              src="/logo.png"
              alt="CostaNov logo"
              className="w-9 h-9 object-contain"
              data-testid="login-mobile-logo"
            />
            <span className="font-display text-xl font-bold tracking-tighter text-black">CostaNov</span>
          </div>
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">
            Autentificare
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-black mb-8">
            Acces cont
          </h2>

          <div className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-black font-semibold text-sm uppercase tracking-wide">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (lock) {
                    setLock(null);
                    setError("");
                  }
                }}
                required
                className="mt-2 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-black font-semibold text-sm uppercase tracking-wide">
                Parola
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 rounded-sm border-zinc-300 focus-visible:ring-black"
                data-testid="login-password-input"
              />
            </div>

            {!locked && remaining > 0 && (
              <div
                className="text-sm text-red-600 border border-red-200 bg-red-50 p-2 rounded-sm"
                data-testid="login-attempts-info"
              >
                Ai {remaining} {remaining === 1 ? "incercare" : "incercari"} de autentificare{" "}
                {remaining === 1 ? "ramasa" : "ramase"}. Dupa {remaining}{" "}
                {remaining === 1 ? "parola gresita" : "parole gresite"} contul se blocheaza temporar.
              </div>
            )}

            {locked && (
              <div
                className="text-sm text-amber-800 border border-amber-300 bg-amber-50 p-2 rounded-sm font-mono"
                data-testid="login-lock-timer"
              >
                Cont blocat temporar. Reincearca in {mm}:{ss}
              </div>
            )}

            {error && !lock && (
              <div
                className="text-sm text-red-600 border border-red-200 bg-red-50 p-2 rounded-sm"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || locked}
              className="w-full rounded-sm bg-black hover:bg-zinc-800 text-white h-11"
              data-testid="login-submit-button"
            >
              {locked ? `Blocat ${mm}:${ss}` : loading ? "Se conecteaza..." : "Intra in cont"}
            </Button>

            <div className="text-xs text-zinc-500 font-mono pt-2">
              Panou intern CostaNov
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
